/**
 * knowledgeStreamClient.ts
 * Shared SSE streaming client for AI Knowledge Base query checkpoints.
 * Dispatches live checkpoint events across the window so both the Avatar HUD
 * and the Admin Test Sandbox can render real-time progress indicators.
 */

export interface KnowledgeCheckpoint {
    stage: number; // 1: DB Fast Path, 2: Doc Analysis (CRAG 1), 3: Context Expansion (Router & Loop 2), 4: Whitelist Web Search / Fallback, 5: Completed
    code: string;
    label: string;
    detail?: any;
    timestamp?: string;
    isCompleted?: boolean;
}

export interface KnowledgeQueryOptions {
    topK?: number;
    enableWebSearch?: boolean;
    onlyWebSearch?: boolean;
    searchMode?: 'fulltext' | 'vector' | 'hybrid';
    onCheckpoint?: (checkpoint: KnowledgeCheckpoint) => void;
}

export async function queryKnowledgeStream(
    idPath: string,
    query: string,
    options: KnowledgeQueryOptions = {}
): Promise<any> {
    const { topK = 6, enableWebSearch = true, onlyWebSearch = false, onCheckpoint } = options;

    const emitEvent = (checkpoint: KnowledgeCheckpoint) => {
        try {
            if (onCheckpoint) {
                onCheckpoint(checkpoint);
            }
            if (typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent("sg3d:knowledge-checkpoint", { detail: checkpoint })
                );
            }
        } catch (err) {
            console.warn("[knowledgeStreamClient] emitEvent error:", err);
        }
    };

    // Initial checkpoint based on search mode
    if (onlyWebSearch) {
        emitEvent({
            stage: 4,
            code: "INIT_WEB_SEARCH",
            label: "Em đang kết nối tới cổng thông tin chính thức...",
            timestamp: new Date().toISOString()
        });
    } else {
        emitEvent({
            stage: 1,
            code: "INIT_QUERY",
            label: "Em đang mở cơ sở tri thức dự án...",
            timestamp: new Date().toISOString()
        });
    }

    const url = `/api/proxy/v1/projects/knowledge/query?id_path=${encodeURIComponent(idPath)}`;
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "text/event-stream"
        },
        body: JSON.stringify({
            query,
            topK,
            enableWebSearch,
            onlyWebSearch,
            searchMode: options.searchMode,
            stream: true
        })
    });

    const contentType = res.headers.get("content-type") || "";

    // Fallback: If server responded with plain JSON instead of SSE
    if (!contentType.includes("text/event-stream") || !res.body) {
        const data = await res.json();
        emitEvent({
            stage: 5,
            code: "COMPLETED",
            label: "Đã tìm thấy thông tin chính xác!",
            isCompleted: true,
            timestamp: new Date().toISOString()
        });
        return data;
    }

    // SSE Reader
    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let finalResult: any = null;

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n\n");
            buffer = lines.pop() || "";

            for (const chunk of lines) {
                const trimmed = chunk.trim();
                if (!trimmed) continue;

                const eventMatch = trimmed.match(/^event:\s*([^\n]+)/m);
                const dataMatch = trimmed.match(/^data:\s*([\s\S]+)$/m);

                const eventType = eventMatch ? eventMatch[1].trim() : "message";
                const rawData = dataMatch ? dataMatch[1].trim() : null;

                if (!rawData) continue;

                try {
                    const parsed = JSON.parse(rawData);
                    if (eventType === "checkpoint") {
                        emitEvent(parsed);
                    } else if (eventType === "result") {
                        finalResult = parsed;
                    }
                } catch (parseErr) {
                    console.warn("[knowledgeStreamClient] JSON parse chunk warning:", parseErr, rawData);
                }
            }
        }

        if (buffer && buffer.trim()) {
            const trimmed = buffer.trim();
            const eventMatch = trimmed.match(/^event:\s*([^\n]+)/m);
            const dataMatch = trimmed.match(/^data:\s*([\s\S]+)$/m);
            const eventType = eventMatch ? eventMatch[1].trim() : "message";
            const rawData = dataMatch ? dataMatch[1].trim() : null;
            if (rawData) {
                try {
                    const parsed = JSON.parse(rawData);
                    if (eventType === "checkpoint") {
                        emitEvent(parsed);
                    } else if (eventType === "result") {
                        finalResult = parsed;
                    }
                } catch (parseErr) {
                    console.warn("[knowledgeStreamClient] remainder parse warning:", parseErr, rawData);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }

    emitEvent({
        stage: 5,
        code: "COMPLETED",
        label: "Đã tìm thấy thông tin chính xác!",
        isCompleted: true,
        timestamp: new Date().toISOString()
    });

    return finalResult || { success: false, found: false, message: "Không nhận được phản hồi từ hệ thống." };
}
